import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAtlas } from "@/context/AtlasContext";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TotalOpsMark } from "@/components/shared/Brand";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MoreHorizontal, LogOut, Keyboard, CircleHelp, UserRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useModes, modeFor, orderMiddleKeys } from "@/components/shell/nav";
import { usePrefs, setRailOrder, resetRailOrder } from "@/hooks/usePrefs";

export function Rail() {
  const modes = useModes();
  const { rail_order } = usePrefs();
  const location = useLocation();
  const current = modeFor(location.pathname);
  const onHome = location.pathname === "/home";
  const [dragKey, setDragKey] = useState(null);

  const home = modes[0];
  const bottom = modes.filter((m) => m.key === "admin");
  const middleModes = modes.filter((m) => m !== home && !bottom.includes(m));
  const orderedKeys = orderMiddleKeys(middleModes.map((m) => m.key), rail_order);
  const middle = orderedKeys.map((k) => middleModes.find((m) => m.key === k)).filter(Boolean);

  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return setDragKey(null);
    const keys = middle.map((m) => m.key);
    const from = keys.indexOf(dragKey), to = keys.indexOf(targetKey);
    keys.splice(to, 0, keys.splice(from, 1)[0]);
    setRailOrder(keys);
    setDragKey(null);
  };

  const Item = ({ m, draggable }) => (
    <Tooltip><TooltipTrigger asChild>
      <NavLink to={m.home} data-testid={`rail-${m.key}`} draggable={draggable}
        onDragStart={draggable ? () => setDragKey(m.key) : undefined}
        onDragOver={draggable ? (e) => e.preventDefault() : undefined}
        onDrop={draggable ? () => onDrop(m.key) : undefined}
        className={cn("rail-item", !onHome && current.key === m.key && "active", dragKey === m.key && "opacity-40")}>
        <span className="rail-icon"><m.icon className="w-[18px] h-[18px]" /></span>
        <span className="rail-label">{m.short || m.label}</span>
      </NavLink>
    </TooltipTrigger><TooltipContent side="right">{m.short || m.label}</TooltipContent></Tooltip>);

  return (
    <div className="w-[68px] h-full flex flex-col items-stretch py-3" style={{ background: "var(--ink)" }} data-testid="rail">
      <NavLink to="/atlas" className="mx-auto mb-4 h-9 flex items-center justify-center text-white" data-testid="rail-logo"><TotalOpsMark className="h-6 w-auto" /></NavLink>
      <nav className="flex-1 flex flex-col gap-1 px-2 overflow-y-auto dark-scroll">
        {home && <Item m={home} draggable={false} />}
        {middle.map((m) => <Item key={m.key} m={m} draggable />)}
      </nav>
      <div className="flex flex-col gap-1 px-2 pt-1">
        {bottom.map((m) => <Item key={m.key} m={m} draggable={false} />)}
        <MoreMenu />
      </div>
    </div>);
}

export function MoreMenu({ mobile }) {
  const { user, logout } = useAtlas();
  const [keys, setKeys] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const initials = user?.name?.split(" ").map((s) => s[0]).join("").slice(0, 2);
  const Row = ({ icon: I, label, onClick, testid }) => <button onClick={onClick} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 text-left" data-testid={testid}><I className="w-4 h-4 text-slate-400" /> {label}</button>;
  const resetOrder = () => { resetRailOrder(); setOpen(false); toast.success("Menu order reset to default"); };
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}><PopoverTrigger asChild>
        <button className={cn("rail-item w-full", mobile && "flex-row gap-2.5 justify-start px-3")} data-testid="rail-more"><span className="rail-icon"><MoreHorizontal className="w-[18px] h-[18px]" /></span><span className="rail-label">More</span></button>
      </PopoverTrigger>
        <PopoverContent side={mobile ? "top" : "right"} align="end" className="w-60 p-1.5">
          <div className="flex items-center gap-2.5 px-3 py-2.5 mb-1 border-b" style={{ borderColor: "var(--line)" }}><span className="w-8 h-8 rounded-full text-white text-xs font-semibold flex items-center justify-center flex-shrink-0" style={{ background: "var(--ink-3)" }}>{initials}</span><div className="min-w-0"><div className="text-sm font-medium truncate">{user?.name}</div><div className="text-[11px] text-slate-400 truncate">{user?.email}</div></div></div>
          <Row icon={UserRound} label="Settings & notifications" onClick={() => { setOpen(false); navigate("/settings"); }} testid="more-profile" />
          <Row icon={RotateCcw} label="Reset menu order" onClick={resetOrder} testid="more-reset-rail" />
          <Row icon={Keyboard} label="Keyboard shortcuts" onClick={() => setKeys(true)} testid="more-shortcuts" />
          <Row icon={CircleHelp} label="Help" onClick={() => { setOpen(false); navigate("/atlas", { state: { q: "How do I use Total Operations?" } }); }} testid="more-help" />
          <div className="my-1 border-t" style={{ borderColor: "var(--line)" }} />
          <Row icon={LogOut} label="Sign out" onClick={logout} testid="logout-button" />
        </PopoverContent></Popover>
      <Dialog open={keys} onOpenChange={setKeys}><DialogContent className="max-w-sm" data-testid="shortcuts-dialog"><DialogHeader><DialogTitle className="font-heading">Keyboard shortcuts</DialogTitle></DialogHeader>
        <div className="text-sm space-y-2">{[["⌘K", "Search everything"], ["⌘J", "Ask Atlas"], ["Enter", "Send to Atlas"], ["Shift + Enter", "New line in Atlas"]].map(([k, l]) => <div key={k} className="flex items-center justify-between"><span className="text-slate-600">{l}</span><kbd className="text-[11px] bg-slate-100 px-2 py-0.5 rounded">{k}</kbd></div>)}</div>
      </DialogContent></Dialog>
    </>);
}
